import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { CouponDuration } from '../../../../enums/coupon-duration';
import type { CouponTemporalUnit } from '../../../../enums/coupon-temporal-unit';
import type { Currency } from '../../../../prop-types/currency-types';
import type { BestActivePromotion, BillingPlan, SubscriptionData } from '../../../../prop-types/subscription-data-types';
import Global from '../../../../store/global-selectors';
import { getDiscountedValues } from '../components/migrate-plan-modal/helpers';
import { useDoesSellerHavePromotionalOffer } from '../components/migrate-plan-modal/hooks/use-does-seller-have-promotional-offer';
import { currentBillingPeriodFilter } from './use-get-coupon-adjusted-monthly-plan-cost';

export type CouponDisplayData = {
  discountPercent: number;
  appliesForMonths: number;
  applicablePlanCodes: string[];
};

type CouponPlan = {
  code: string;
  name: string;
};

type TargetCoupon = {
  discountPercent: number;
  appliesForMonths: number;
  plans?: CouponPlan[];
};

type PromotionUpgrade = {
  targetCoupon?: TargetCoupon;
  targetBillingPlan?: unknown;
};

export type SubscriptionCouponPlan = {
  code: string;
  name: string;
  billingPlanId: string;
};

export type SubscriptionCouponDetails = {
  appliesForMonths: number | undefined;
  createdAt: string | undefined;
  discountInCents: number | undefined;
  discountPercent: number | undefined;
  discountType: 'Percent' | 'Fixed' | undefined;
  duration: CouponDuration | undefined;
  freeTrialAmount: number | undefined;
  freeTrialUnit: string | undefined;
  hostedDescription: string | undefined;
  invoiceDescription: string | undefined;
  isAppliedToInvoice: boolean | undefined;
  lastApplicableBillingCycleDate: string | undefined;
  plans: SubscriptionCouponPlan[];
  temporalAmount: number | undefined;
  temporalUnit: CouponTemporalUnit | undefined;
};

export type SubscriptionCouponRedemption = {
  discounted: Currency;
  coupon: SubscriptionCouponDetails;
};

export type ActiveCouponTotals = {
  totalDiscountPercent: number;
  totalDiscountInCents: number;
  hasCoupons: boolean;
  couponCount: number;
};

export type PlanDiscountResult = {
  hasDiscount: boolean;
  discountedPrice: number;
  savingsAmount: number;
  discountPercent: number;
  appliesForMonths: number;
};

type ReduxState = {
  seller: unknown;
};

/**
 * Hook to get active promo coupon from promotionUpgrade data source
 * @returns CouponDisplayData or undefined if no promotion active
 */
export const useActivePromoCoupon = (): CouponDisplayData | undefined => {
  const hasPromotionalOffer = useDoesSellerHavePromotionalOffer();
  const promotionUpgrade: PromotionUpgrade | null = useSelector((state: ReduxState) => Global.selectSellerPromotionUpgrade(state));

  return useMemo(() => {
    if (!hasPromotionalOffer) {
      return undefined;
    }

    const targetCoupon = promotionUpgrade?.targetCoupon;
    if (!targetCoupon?.discountPercent) {
      return undefined;
    }

    return {
      discountPercent: targetCoupon.discountPercent,
      appliesForMonths: targetCoupon.appliesForMonths,
      applicablePlanCodes: targetCoupon?.plans?.map((plan) => plan.code) ?? [],
    };
  }, [hasPromotionalOffer, promotionUpgrade?.targetCoupon]);
};

/**
 * Hook to check if a promotion is currently active
 * @returns boolean indicating if promotion is active
 */
export const useHasActivePromotion = (): boolean => {
  const coupon = useActivePromoCoupon();
  return !!coupon;
};

export const getCouponDisplayDataFromBestActivePromotion = (
  bestActivePromotion: BestActivePromotion | null | undefined,
): CouponDisplayData | undefined => {
  const coupon = bestActivePromotion?.coupon;
  if (!coupon?.discountPercent) {
    return undefined;
  }

  return {
    discountPercent: coupon.discountPercent,
    appliesForMonths: coupon.appliesForMonths,
    applicablePlanCodes: coupon.plans?.map((plan) => plan.code) ?? [],
  };
};

const findPlanByCode = (plans: BillingPlan[] | null | undefined, planCode: string): BillingPlan | undefined =>
  plans?.find((plan) => plan.code?.toLowerCase() === planCode.toLowerCase());

export const useBestActivePromotionForPlan = (planCode: string): CouponDisplayData | undefined => {
  const subscriptionData: SubscriptionData | null = useSelector((state: ReduxState) => Global.selectSellerSubscriptionData(state));

  return useMemo(() => {
    if (!planCode || !subscriptionData) {
      return undefined;
    }

    const matchedPlan =
      findPlanByCode(subscriptionData.allPlans, planCode) ?? findPlanByCode(subscriptionData.defaultProfilePlans, planCode);

    return getCouponDisplayDataFromBestActivePromotion(matchedPlan?.bestActivePromotion);
  }, [planCode, subscriptionData]);
};

/**
 * Hook to calculate discount for a specific plan
 * @param planCode - The billing plan code to check
 * @param fullPrice - The full price before discount
 * @param coupon - Optional coupon override (defaults to active coupon)
 * @returns PlanDiscountResult or undefined if no discount applies
 */
export const usePlanDiscount = (planCode: string, fullPrice: number, coupon?: CouponDisplayData | null): PlanDiscountResult | undefined => {
  // theoretically we should be able to rely on just bestActivePromo for this hook, but I don't want to make that jump quite yet.
  const bestActivePromo = useBestActivePromotionForPlan(planCode);
  const globalActiveCoupon = useActivePromoCoupon();
  const effectiveCoupon = coupon ?? bestActivePromo ?? globalActiveCoupon;

  return useMemo(() => {
    if (!effectiveCoupon || !planCode) {
      return undefined;
    }

    const isPlanEligible =
      effectiveCoupon.applicablePlanCodes.length === 0 ||
      effectiveCoupon.applicablePlanCodes.some((code) => code.toLowerCase() === planCode.toLowerCase());

    if (!isPlanEligible) {
      return undefined;
    }

    const discountPercentage = effectiveCoupon.discountPercent / 100;
    const { discountedPrice, discountedSaving } = getDiscountedValues({
      fullPrice,
      discountPercentage,
    });

    return {
      hasDiscount: true,
      discountedPrice,
      savingsAmount: discountedSaving,
      discountPercent: effectiveCoupon.discountPercent,
      appliesForMonths: effectiveCoupon.appliesForMonths,
    };
  }, [effectiveCoupon, planCode, fullPrice]);
};

const ROUND_TO_NEAREST_HALF = 2;
const PERCENT_MULTIPLIER = 100;
const MONTHS_PER_YEAR = 12;

/**
 * Hook to calculate annual savings percentage vs monthly
 * @param monthlyPlanPrice - Monthly plan price
 * @param annualPlanPrice - Annual plan price (total for year)
 * @returns Savings percentage rounded to nearest 0.5%
 */
export const useAnnualSavingsPercent = (monthlyPlanPrice: number, annualPlanPrice: number): number => {
  return useMemo(() => {
    if (monthlyPlanPrice <= 0 || annualPlanPrice <= 0) {
      return 0;
    }

    const monthlyEquivalent = monthlyPlanPrice * MONTHS_PER_YEAR;
    const savings = ((monthlyEquivalent - annualPlanPrice) / monthlyEquivalent) * PERCENT_MULTIPLIER;
    const roundedSavings = Math.round(savings * ROUND_TO_NEAREST_HALF) / ROUND_TO_NEAREST_HALF;

    return roundedSavings;
  }, [monthlyPlanPrice, annualPlanPrice]);
};

/**
 * Hook to get subscription coupons from manageSubscription response
 * @returns Array of subscription coupons
 */
export const useSubscriptionCoupons = (): SubscriptionCouponDetails[] => {
  return useSelector(Global.selectSellerSubscriptionCoupons) as SubscriptionCouponDetails[];
};

const DEFAULT_COUPON_TOTALS: ActiveCouponTotals = {
  totalDiscountPercent: 0,
  totalDiscountInCents: 0,
  hasCoupons: false,
  couponCount: 0,
};

/**
 * Hook to get aggregated totals from all active subscription coupons
 * @returns ActiveCouponTotals with summed discount values
 */
export const useActiveCouponTotals = (): ActiveCouponTotals => {
  const coupons = useSubscriptionCoupons();

  return useMemo(() => {
    if (!coupons?.length) {
      return DEFAULT_COUPON_TOTALS;
    }

    const totals = coupons.reduce(
      (acc, coupon) => ({
        totalDiscountPercent: acc.totalDiscountPercent + (coupon.discountPercent ?? 0),
        totalDiscountInCents: acc.totalDiscountInCents + (coupon.discountInCents ?? 0),
        hasCoupons: true,
        couponCount: acc.couponCount + 1,
      }),
      DEFAULT_COUPON_TOTALS,
    );

    return totals;
  }, [coupons]);
};

export type EffectivePlanDiscountResult = {
  effectiveDiscount: PlanDiscountResult | null;
  isCouponDiscount: boolean;
};

export const useEffectivePlanDiscount = (planCode: string, fullPrice: number): EffectivePlanDiscountResult => {
  const promoDiscount = usePlanDiscount(planCode, fullPrice);
  const subscriptionCoupon = useCouponForPlanCode(planCode);

  return useMemo(() => {
    const applicableCoupon = subscriptionCoupon && currentBillingPeriodFilter(subscriptionCoupon) ? subscriptionCoupon : null;

    if (!applicableCoupon?.discountPercent) {
      return { effectiveDiscount: promoDiscount ?? null, isCouponDiscount: false };
    }

    // Subscription coupon wins when it matches or beats the promo — it's what changeplan uses
    const couponWins = applicableCoupon.discountPercent >= (promoDiscount?.discountPercent ?? 0);

    if (!couponWins) {
      return { effectiveDiscount: promoDiscount ?? null, isCouponDiscount: false };
    }

    const { discountedPrice, discountedSaving } = getDiscountedValues({
      fullPrice,
      discountPercentage: applicableCoupon.discountPercent / 100,
    });

    return {
      effectiveDiscount: {
        hasDiscount: true,
        discountedPrice,
        savingsAmount: discountedSaving,
        discountPercent: applicableCoupon.discountPercent,
        appliesForMonths: applicableCoupon.appliesForMonths ?? 0,
      },
      isCouponDiscount: true,
    };
  }, [promoDiscount, subscriptionCoupon, fullPrice]);
};

// Returns highest bestActivePromotion.coupon.discountPercent across all annual plans
export const useMaxAnnualSavingsPercent = (referencePlans: BillingPlan[]): number =>
  useMemo(() => {
    const annualPlans = referencePlans.filter((p) => p.isOnAnnualBilling);
    return Math.max(0, ...annualPlans.map((p) => p.bestActivePromotion?.coupon?.discountPercent ?? 0));
  }, [referencePlans]);

export const computeShouldShowAnnualToggle = (referencePlans: BillingPlan[], activePromoCoupon: CouponDisplayData | undefined): boolean => {
  const annualPlans = referencePlans.filter((p) => p.isOnAnnualBilling);
  const promoDiscountPercent = activePromoCoupon?.discountPercent ?? 0;
  return (
    annualPlans.length > 0 &&
    (promoDiscountPercent === 0 || !annualPlans.every((p) => promoDiscountPercent >= (p.bestActivePromotion?.coupon?.discountPercent ?? 0)))
  );
};

export const useShouldShowAnnualToggle = (referencePlans: BillingPlan[]): boolean => {
  const activePromoCoupon = useActivePromoCoupon();
  return useMemo(() => computeShouldShowAnnualToggle(referencePlans, activePromoCoupon), [referencePlans, activePromoCoupon]);
};

/**
 * Hook to get coupon details for a specific plan code
 * Returns the first matching coupon where plans array is empty (applies to all) or contains the plan code
 * @param planCode - The billing plan code to check
 * @returns SubscriptionCouponDetails or null if no coupon applies
 */
export const useCouponForPlanCode = (planCode: string): SubscriptionCouponDetails | undefined => {
  const coupons = useSubscriptionCoupons();

  return useMemo(() => {
    if (!coupons?.length || !planCode) {
      return undefined;
    }

    const matchingCoupon = coupons.find((coupon) => {
      const { plans } = coupon;
      if (!plans?.length) {
        return true;
      }
      return plans.some((plan) => plan.code.toLowerCase() === planCode.toLowerCase());
    });

    return matchingCoupon ?? undefined;
  }, [coupons, planCode]);
};
